import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetGroupedEntitiesDocument } from "~/graphql/supergraphHooks";
import {
  type GetGroupedEntitiesQuery,
  type ClientArenaFilter,
  type ClientArenaSort,
} from "~/graphql/supergraphTypes";
import { PAGINATION } from "~/pages/Billing/constants";

type FilterInfo = {
  filterType: "text" | "set" | "date";
  filter?: string;
  values?: string[];
  dateFrom?: string;
  type?: string;
};

const isFilterInfo = (value: unknown): value is FilterInfo => {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.filterType === "string" &&
    (obj.filterType === "text" || obj.filterType === "set" || obj.filterType === "date")
  );
};

const toClientArenaFilter = (filterInfo: FilterInfo): ClientArenaFilter | null => {
  if (filterInfo.filterType !== "text" || !filterInfo.filter) {
    return null;
  }
  return { field: "clientName", filterValues: [filterInfo.filter] };
};

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
  groupKeys: string[] = [],
): ClientArenaFilter[] | null => {
  if (groupKeys.length > 0) {
    return null;
  }

  if (!filterModel) {
    return null;
  }
  if ("filterType" in filterModel) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }

  const queryFilters: ClientArenaFilter[] = [];

  for (const [field, filterInfo] of Object.entries(filterModel)) {
    if (field !== "clientId") continue;
    if (!isFilterInfo(filterInfo)) continue;
    const filter = toClientArenaFilter(filterInfo);
    if (filter) queryFilters.push(filter);
  }

  return queryFilters.length > 0 ? queryFilters : null;
};

export const getSortVars = (sortItems: AgGrid.SortModelItem[]): ClientArenaSort[] | null => {
  if (!sortItems || sortItems.length === 0) {
    return null;
  }
  return sortItems.map((item) => ({
    field: item.colId,
    sortAscending: item.sort === "asc",
  }));
};

export type GroupedEntityRow = {
  clientId: string;
  clientName: string | null;
  billingGroupId: string | null;
  billingGroupName: string | null;
  entityId: number | null;
  entityName: string | null;
  type: "client" | "billingGroup" | "entity";
  softwareEumUsd: number | null;
  billableFaEumUsd: number | null;
  jsqProductLine: string | null;
  billingStatus: string | null;
  hasWarning: boolean;
  childCount: number;
};

const isValidRowType = (value: string): value is GroupedEntityRow["type"] => {
  return value === "client" || value === "billingGroup" || value === "entity";
};

export const rowsFromQuery = (
  rows: GetGroupedEntitiesQuery["getGroupedEntities"]["rows"] | null | undefined,
): GroupedEntityRow[] => {
  if (!rows) {
    return [];
  }
  return rows
    .filter((row) => isValidRowType(row.type))
    .map((row) => ({
      clientId: row.clientId,
      clientName: row.clientName ?? null,
      billingGroupId: row.billingGroupId ?? null,
      billingGroupName: row.billingGroupName ?? null,
      entityId: row.entityId ?? null,
      entityName: row.entityName ?? null,
      type: row.type as GroupedEntityRow["type"],
      softwareEumUsd: row.softwareEumUsd ?? null,
      billableFaEumUsd: row.billableFaEumUsd ?? null,
      jsqProductLine: row.jsqProductLine ?? null,
      billingStatus: row.billingStatus ?? null,
      hasWarning: row.hasWarning,
      childCount: row.childCount,
    }));
};

export type AllClientEntitiesDataSourceProps = {
  onTotalCountChange?: (count: number) => void;
};

export class AllClientEntitiesDataSource implements AgGrid.IServerSideDatasource {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  asOfDate: string;
  private onTotalCountChange?: AllClientEntitiesDataSourceProps["onTotalCountChange"];

  constructor(
    props?: AllClientEntitiesDataSourceProps,
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
    asOfDate: string = "",
  ) {
    this.apolloClient = apolloClient;
    this.asOfDate = asOfDate;
    this.onTotalCountChange = props?.onTotalCountChange;
  }

  public async getRows(
    params: AgGrid.IServerSideGetRowsParams<GroupedEntityRow>,
  ): Promise<void> {
    const groupKeys = params.request.groupKeys ?? [];
    const filters = getFilterVars(params.request.filterModel, groupKeys);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const limit =
      (params.request.endRow ?? 0) - offset || PAGINATION.ALL_CLIENT_ENTITIES_PAGE_SIZE;

    try {
      const response = await this.apolloClient.query<GetGroupedEntitiesQuery>({
        query: GetGroupedEntitiesDocument,
        variables: {
          asOfDate: this.asOfDate,
          groupKeys,
          offset,
          limit,
          filters,
          sorts,
        },
        fetchPolicy: "no-cache",
      });

      const rows = rowsFromQuery(response.data.getGroupedEntities.rows);
      const totalCount = response.data.getGroupedEntities.totalCount;

      if (groupKeys.length === 0 && this.onTotalCountChange) {
        this.onTotalCountChange(totalCount);
      }

      const rowCount = groupKeys.length === 0 ? totalCount : undefined;

      params.success({
        rowData: rows,
        rowCount,
      });

      if (groupKeys.length === 0 && totalCount === 0) {
        params.api?.showNoRowsOverlay();
      } else if (groupKeys.length === 0) {
        params.api?.hideOverlay();
      }
    } catch {
      params.fail();
    }
  }
}
